import React from 'react'
import styled from 'styled-components'
import { Card, Row, Col } from 'react-bootstrap'
import { Activity, Clock, Users, Zap } from 'lucide-react'
import { Model } from '../../../../types/models/model'
import ModelItem from '../../../styled/ModelSelect/ModelItem'
import PerformanceCard from '../../ModelsOverview/components/PerformanceCard'
import ClassStatistics from './ClassStatistics'
import { formatDuration, formatResponseTime } from '../../../../utils/timeUtils'
import { toTitleCase } from '../../../../utils'

const OverviewContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const ModelSection = styled.div`
  flex: 0 0 auto;
  width: 350px;
  margin-right: 2rem;
`

const StatsSection = styled.div`
  flex: 1;
  background: rgba(147, 51, 234, 0.05);
  border: 1px solid rgba(147, 51, 234, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  min-width: 0; // Prevent flex item from overflowing
`

const ContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 2rem;
`

const StatsCard = styled(Card)`
  background: #2a2a2a;
  border: none;
  border-radius: 12px;
  padding: 1.25rem;
  height: 100%;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  .card-title {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    svg {
      width: 18px;
      height: 18px;
      opacity: 0.7;
    }
  }

  .stat-value {
    font-size: 1.5rem;
    font-weight: 500;
    color: #fff;
    margin-bottom: 0.5rem;
  }

  .stat-description {
    font-size: 0.85rem;
    color: #808080;
  }
`

const SectionTitle = styled.h2`
  color: #fff;
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);
  }
`

interface DeploymentOverviewProps {
  model?: Model
}

const DeploymentOverview: React.FC<DeploymentOverviewProps> = ({ model }) => {
  if (!model?.deployment) {
    return (
      <div className="text-center p-5">
        <h5 className="text-muted">No deployment information available</h5>
        <p className="text-muted">Deploy your model to see statistics and metrics</p>
      </div>
    )
  }

  return (
    <OverviewContainer>
      <ContentRow>
        <StatsSection>
          <Row>
            <Col xs={5}>
              <div style={{ marginTop: '1rem' }}>
                <ModelItem model={model} $minimized={false} />
              </div>
              <div className="mt-3">
                <PerformanceCard model={model} small />
              </div>
            </Col>
            <Col xs={1}></Col>
            <Col xs={6}>
              <div style={{ marginTop: '1rem' }}>
                <SectionTitle>Deployment Statistics</SectionTitle>
                <Row className="g-4">
                  <Col md={6}>
                    <StatsCard>
                      <Card.Title>
                        <Activity /> Current Status
                      </Card.Title>
                      <div className="stat-value">
                        {toTitleCase(model.deployment.status?.replace('_', ' ') || 'Unknown')}
                      </div>
                      <div className="stat-description">Current deployment status</div>
                    </StatsCard>
                  </Col>

                  <Col md={6}>
                    <StatsCard>
                      <Card.Title>
                        <Users /> Deployed At
                      </Card.Title>
                      <div className="stat-value">
                        {new Date(model.deployment.deployed_at).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true,
                        })}
                      </div>
                      <div className="stat-description">
                        {formatDuration(
                          (Date.now() - new Date(model.deployment.deployed_at).getTime()) * 1000
                        )}{' '}
                        ago
                      </div>
                    </StatsCard>
                  </Col>

                  <Col md={6}>
                    <StatsCard>
                      <Card.Title>
                        <Zap /> Total API Calls
                      </Card.Title>
                      <div className="stat-value">{model.deployment.total_invocations}</div>
                      <div className="stat-description">API calls</div>
                    </StatsCard>
                  </Col>

                  <Col md={6}>
                    <StatsCard>
                      <Card.Title>
                        <Clock /> Running Average Response Time
                      </Card.Title>
                      <div className="stat-value">
                        {formatResponseTime(model.deployment.avg_invocation_rate)}
                      </div>
                      <div className="stat-description">Average inference time per image</div>
                    </StatsCard>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
          <Row className="mt-4">
            <Col xs={12}>
              <ClassStatistics class_statistics={model.deployment.class_statistics} />
            </Col>
          </Row>
        </StatsSection>
      </ContentRow>
    </OverviewContainer>
  )
}

export default DeploymentOverview
